<template>
  <div :style="appStyleProps">
    <div data-cy="nav-menu" id="nav-menu-container" :style="position" @click.stop>
      <ul>
        <div class="border-bottom border-200" v-if="borrowers.length > 1">
          <div class="py-4 px-6 mt-1 w-full text-start text-truncate">Switch Business</div>
          <li v-for="borrower in borrowers" :key="borrower.id">
            <a v-if="borrower.id !== currentBorrower?.id"
              @click="businessSwitchStore.switchTo(borrower); close()"
              :data-cy="`switch-business-${borrower.id}`"
              href="javascript:;"
              class="d-flex align-middle py-4 px-6 text-start align-items-center">
              {{ borrower.name }}
            </a>
          </li>
        </div>
        <div v-if="links?.length" class="border-bottom border-200">
          <li v-for="link in links"
            :key="kebabString(link.title)"
            :style="{ cursor: 'pointer' }">
            <router-link
              :data-cy="'nav-dropdown-' + kebabString(link.title)"
              v-if="link.url" :to="link.url"
              @click="close()"
              class="d-flex align-middle py-4 px-6 text-start align-items-center"
              :class="{ 'current': checkCurrentPath(link.url) }">
              <NuxtImg v-if="link.imgIcon" class="align-middle mb-1 me-2" :src="formatImagePath(link.imgIcon)" />
              <font-awesome-icon v-else :icon="farIconMap[link.faIcon]" class="fa-fw me-2"></font-awesome-icon>
              {{ link.title }}
            </router-link>
          </li>
        </div>
        <li v-if="!rootStore.isEmbedded">
          <a data-cy="logout"
            class="d-block align-middle py-4 px-6 text-start"
            @click="logout($event)"
            id="app-nav-sign-out">
            <font-awesome-icon :icon="faXmark" class="fa-fw me-2"></font-awesome-icon>
            Sign Out
          </a>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { mapState } from 'pinia'
import { PORTAL_NAV_URLS } from '~/libs/portal-constants'
import {
  faBorderAll,
  faFileInvoiceDollar,
  faFolder,
  faHouse,
  faSackDollar,
  faUserGear,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons'

const farIconMap = {
  'border-all': faBorderAll,
  'file-invoice-dollar': faFileInvoiceDollar,
  'folder': faFolder,
  'house': faHouse,
  'sack-dollar': faSackDollar,
  'user-gear': faUserGear,
}
import { useAffiliateCustomizationStore } from '~/store/affiliate-customization'
import { useBorrowerStore } from '~/store/borrower'
import { useBusinessSwitchStore } from '~/store/business-switch'
import { useIdentityStore } from '~/store/identity'
import { usePortalStore } from '~/store/portal'
import { useRootStore } from '~/store/root'
import formatImagePath from '~/libs/image-format'
import { get } from 'es-toolkit/compat'

export default {
  name: 'NavMenu',
  props: {
    hideAppLinks: {
      type: Boolean,
      default: false,
    },
    showSettings: {
      type: Boolean,
      default: true,
    },
    position: {
      type: Object,
      default: () => ({
        position: 'absolute',
        top: '55px',
        right: '0px',
      }),
    }
  },
  setup () {
    return {
      businessSwitchStore: useBusinessSwitchStore(),
      farIconMap,
      faXmark,
      formatImagePath,
      identityStore: useIdentityStore(),
      rootStore: useRootStore(),
    }
  },
  computed: {
    ...mapState(useAffiliateCustomizationStore, ['affiliateCustomizations', 'appStyleProps']),
    ...mapState(useRootStore, {
      borrowers: store => get(store, 'authUser.borrowers', []),
      isEmbedded: store=> get(store, 'isEmbedded'),
    }),
    ...mapState(usePortalStore, ['navigationItems', 'settingsNavigationItem']),
    ...mapState(useBorrowerStore, {
      applicationComplete: store =>  get(store, 'borrower.applicationComplete'),
      currentBorrower: store => get(store, 'borrower'),
    }),
    links() {
      return this.hideAppLinks
        ? this.settingsNavigationItem ? [this.settingsNavigationItem] : []
        : this.navigationItems.filter(link => {
          if (!link) return false
          return !link.requiresAppComplete ?? true
            ? true
            : this.isApplicationComplete
        })
    },
    isApplicationComplete() {
      return this.isEmbedded ? true : this.applicationComplete
    }
  },
  methods: {
    close() {
      this.$emit('close')
    },
    kebabString(str) {
      return str.replace(/\s/g , "-").toLowerCase()
    },
    async logout($event) {
      $event.stopPropagation();
      await this.identityStore.logout()
    },
    checkCurrentPath(linkUrl) {
      return linkUrl === PORTAL_NAV_URLS.OFFERS
        ? this.$route.path.includes(linkUrl)
        : this.$route.path === linkUrl
    },
  }
}
</script>

<style lang="scss" scoped>
#nav-menu-container {
  z-index: 10000;
  width: 280px;
  margin: 0;
  background: #FFF;
  box-shadow: 0px 5px 14px rgba(0, 0, 0, 0.2);
  border-radius: 14px;
  overflow: hidden;
  ul {
    padding: 0;
    margin: 0;
    list-style: none;
    li {
      a {
        color: $app-body-primary-font-color !important;
        &:hover {
          text-decoration: none;
          color: $app-body-primary-font-color !important;
          background: $background-100;
        }
        &.current {
          font-weight: bold;
          color: $app-card-label-font-color !important;
          background: $app-button-background-color;
          i, svg {
            color: $app-card-label-font-color;
          }
        }
        img {
          display: inline-block;
          width: 18px;
        }
      }
    }
  }
}
</style>
